import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { endpointsApi } from '@/services/api';
import { trackEvent } from '@/lib/analytics';
import type { CreateEndpointInput, UpdateEndpointInput } from '@/types';

export const useEndpoints = (page = 1, limit = 20, filters?: { method?: string; collaborationConfigId?: string; search?: string }) => {
  return useQuery({
    queryKey: ['endpoints', page, limit, filters],
    queryFn: () => endpointsApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const useEndpoint = (id: string | undefined) => {
  return useQuery({
    queryKey: ['endpoint', id],
    queryFn: () => endpointsApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

/**
 * Requests for an endpoint.
 *
 * innovate-service captures gate-rejected calls as well as runs (task 717), so
 * both hooks below ask for `accepted` only — the Runs views describe what ran.
 * Surfacing rejections in the UI is a separate, open product decision.
 */
export const useEndpointRequests = (endpointId: string | undefined, page = 1, limit = 20) => {
  return useQuery({
    queryKey: ['endpoint-requests', endpointId, page, limit, 'accepted'],
    queryFn: () => endpointsApi.getRequests(endpointId!, page, limit, 'accepted').then(res => res.data),
    enabled: !!endpointId,
  });
};

export const useEndpointRequestCount = (endpointId: string | undefined) => {
  return useQuery({
    queryKey: ['endpoint-request-count', endpointId, 'accepted'],
    queryFn: () => endpointsApi.getRequests(endpointId!, 1, 1, 'accepted').then(res => res.data.pagination.total),
    enabled: !!endpointId,
    staleTime: 60000, // Cache for 1 minute
  });
};

export const useEndpointResponses = (endpointId: string | undefined, requestId: string | undefined, page = 1, limit = 20) => {
  return useQuery({
    queryKey: ['endpoint-responses', endpointId, requestId, page, limit],
    queryFn: () => endpointsApi.getResponses(endpointId!, requestId!, page, limit).then(res => res.data),
    enabled: !!endpointId && !!requestId,
  });
};

/**
 * Create an endpoint.
 *
 * Deliberately has NO `onError` (task 388): the mutation rejects and the
 * failure travels to whoever called `mutateAsync`, so the form can decide
 * between an inline field error (a rejected path) and a toast. Swallowing it
 * here is what made a duplicate-path create look like nothing happened.
 */
export const useCreateEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEndpointInput) =>
      endpointsApi.create(data).then(res => res.data.data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
      // `ai_service_created` (task 427) for the hand-built path — the new
      // AI Service form. Emitted from the mutation rather than the page so any
      // caller of it is covered. The Assistant conversation (the sidebar and
      // the New AI Service page, task 1117) builds its services server-side and
      // emits its own event in `AssistantProvider`.
      //
      // `service_type` records which surface produced the service (the app has
      // exactly one kind of AI service — a dynamic endpoint), which is the
      // dimension worth comparing. Ids and shape flags only: path and
      // description are user-authored text.
      trackEvent('ai_service_created', {
        service_id: created.id,
        service_type: 'form',
        method: created.method,
        mcp_exposed: created.mcpExposed === true,
      });
    },
  });
};

/** Update an endpoint. Same rejection contract as `useCreateEndpoint`. */
export const useUpdateEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEndpointInput }) =>
      endpointsApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
      queryClient.invalidateQueries({ queryKey: ['endpoint', variables.id] });
    },
  });
};

export const useDeleteEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpointsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    },
  });
};

// ============================================================================
// Stop and resume (task 1164, against task 1163's routes)
// ============================================================================

/**
 * Everything that reads a service's stop state: the page (`['endpoint', id]`),
 * every list and picker (`['endpoints']`), and the history card.
 */
const invalidateStopState = (queryClient: ReturnType<typeof useQueryClient>, id: string) => {
  queryClient.invalidateQueries({ queryKey: ['endpoint', id] });
  queryClient.invalidateQueries({ queryKey: ['endpoints'] });
  queryClient.invalidateQueries({ queryKey: ['endpoint-stops', id] });
};

/**
 * Stop an AI Service. No `onError`: the rejection travels to `mutateAsync` so
 * the caller shows the service's own refusal (the task-858 rule).
 */
export const useStopEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      endpointsApi.stopEndpoint(id, reason).then(res => res.data.data),
    onSuccess: (_, variables) => invalidateStopState(queryClient, variables.id),
  });
};

/** Resume an AI Service. Same rejection contract as `useStopEndpoint`. */
export const useResumeEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpointsApi.resumeEndpoint(id).then(res => res.data.data),
    // Schedules do NOT resume with the service (the owner resumes them one by
    // one); each row's Resume is enabled from `endpoint.stop`, which this
    // refetch clears.
    onSuccess: (_, id) => invalidateStopState(queryClient, id),
  });
};

/** The stop history, most recent first. */
export const useEndpointStops = (endpointId: string | undefined) => {
  return useQuery({
    queryKey: ['endpoint-stops', endpointId],
    queryFn: () =>
      endpointsApi.listEndpointStops(endpointId!).then(res => res.data.data ?? []),
    enabled: !!endpointId,
  });
};

/** How far back the refused-since count reads. Past this it says "N+". */
export const REFUSED_SINCE_STOP_WINDOW = 100;

/**
 * Calls refused since the open stop began.
 *
 * The read shape carries no counter, so this reads the refusals the service
 * already captures (task 717) — `status=rejected&rejectionCode=service_stopped`,
 * newest first — and counts the rows at or after `stoppedAt`. Earlier stops'
 * refusals carry the same code, which is why the date check is needed. One page
 * of `REFUSED_SINCE_STOP_WINDOW` rows; `capped` says the true count may be higher.
 */
export const useRefusedSinceStop = (
  endpointId: string | undefined,
  stoppedAt: string | undefined
) => {
  return useQuery({
    queryKey: ['endpoint-refused-since-stop', endpointId, stoppedAt],
    queryFn: async () => {
      const res = await endpointsApi.getRequests(
        endpointId!,
        1,
        REFUSED_SINCE_STOP_WINDOW,
        'rejected',
        'service_stopped'
      );
      const since = new Date(stoppedAt!).getTime();
      const rows = res.data.data ?? [];
      const count = rows.filter(row => new Date(row.createdAt).getTime() >= since).length;
      return { count, capped: count >= REFUSED_SINCE_STOP_WINDOW };
    },
    enabled: !!endpointId && !!stoppedAt,
    staleTime: 30000,
  });
};
